<template>
  <div
    class="search-area"
    ref="searchArea"
    :style="removeMargin ? { '--text-input-margin-bottom': 0 } : null"
  >
    <dropdown
      flex
      align="left"
      :active="active"
      @close="active = false"
    >
      <div class="input-wrapper">
        <textarea
          v-model.trim="localValue"
          ref="input"
          rows="1"
          maxlength="10000"
          :placeholder="placeholder"
          :autocomplete="options ? 'off' : 'on'"
          @input="update"
          @keydown.enter.prevent
          @focus="handleFocus"
        />

        <span v-if="loading" class="icon-spot">
          <span class="spinner" />
        </span>

        <div v-else class="icon-spot">
          <icon-button
            size="20"
            :icon="rightIcon"
            :hidden="isRightIconHidden"
            @click="handleRightIcon"
          />
        </div>
      </div>

      <template #menu>
        <dropdown-menu
          hide-no-results
          :options="filteredOptions"
          :activeValue="value"
          @select="handleSelect"
        />
      </template>
    </dropdown>
  </div>
</template>

<script>
import Dropdown from './Dropdown';
import DropdownMenu from './DropdownMenu';
import IconButton from './IconButton';

export default {
  components: {
    Dropdown,
    DropdownMenu,
    IconButton,
  },

  props: {
    value: String,
    placeholder: {
      type: String,
      default: 'Search',
    },
    removeMargin: Boolean,
    options: Array,
    loading: Boolean,
    icon: String,
  },

  data() {
    return {
      active: false,
      localValue: this.value,
    };
  },

  mounted() {
    this.resize();
  },

  watch: {
    value(value) {
      this.localValue = value;
    },

    localValue() {
      this.$nextTick(() => {
        this.resize();
      });
    },
  },

  computed: {
    filteredOptions() {
      if (!this.options) return [];

      return this.options.filter(({ label }) => label.toLowerCase().indexOf(this.value?.toLowerCase()) > -1);
    },

    rightIcon() {
      return this.localValue || !this.icon ? 'close' : this.icon;
    },

    isRightIconHidden() {
      return !this.localValue && !this.icon;
    },
  },

  methods: {
    focus() {
      this.$refs.input.focus();
    },

    update() {
      let value = this.localValue;

      this.$emit('input', value);
    },

    clear() {
      this.localValue = '';
      this.update();
    },

    handleSelect(value) {
      this.$emit('input', value);
      this.active = false;
    },

    handleFocus() {
      this.active = true;
    },

    handleRightIcon() {
      if (this.rightIcon === 'close') {
        this.clear();
      } else {
        this.$emit('iconClick');
      }
    },

    resize() {
      const height = this.localValue && this.localValue.length > 40 ? this.$refs.input.scrollHeight + 2 : 20;

      this.$refs.input.style.height = `${height}px`;
      this.$refs.searchArea.style.height = `${height}px`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../scss/variables.scss';

.search-area {
  height: 48px;
  min-height: 48px;
  max-height: 300px;
  margin-bottom: var(--text-input-margin-bottom, $gp);
  position: relative;
  max-width: 560px;
  --theme-icon-button-color: #{$color-gray-5};
  --text-input-margin-bottom: 0;

  @media($medium-down) {
    max-width: none;
  }

  .dropdown,
  .input-wrapper {
    width: 100%;
  }

  .input-wrapper {
    height: 100%;
  }

  textarea {
    background-color: var(--theme-background);
    border: solid 1px var(--theme-input-border);
    border-radius: $border-radius;
    color: var(--theme-color);
    outline: 0;
    font-size: 16px;
    padding: 12px $gp * 2 $gp * .5 $gp * .75;
    width: 100%;
    height: 48px;
    min-height: 48px;
    max-height: 300px;
    resize: none;

    &:focus {
      border-color: $color-green-1;
    }
  }
}

.icon-spot {
  position: absolute;
  height: 48px;
  display: inline-flex;
  align-items: center;
  right: 0;
  padding-right: $gp * .5;
}

.spinner {
  display: block;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  transform: translateZ(0);
  animation: spin 1s infinite linear;
  border-top: 2px solid $color-green-1;
  border-right: 2px solid $color-green-1;
  border-bottom: 2px solid $color-green-1;
  border-left: 2px solid transparent;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
